import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Link,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  getApplicationLink,
  listApplications,
  getApplication,
  updateApplication,
  generateTenancyAgreement,
  uploadTenancyAgreement,
  type RentalApplicationSummary,
} from '../services/rentalApplicationService';

const statusColor: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
  submitted: 'primary',
  pending_review: 'warning',
  shortlisted: 'primary',
  approved: 'success',
  rejected: 'error',
};

/** Human-readable labels for application form fields (primary applicant + extra). */
const APPLICATION_FIELD_LABELS: Record<string, string> = {
  // Property & lease
  propertyAddress: 'Property address',
  suburb: 'Suburb',
  postcode: 'Postcode',
  weeklyRent: 'Weekly rent ($)',
  advertisedWeeklyRent: 'Advertised weekly rent ($)',
  bondAmount: 'Bond amount ($)',
  proposedLeaseStartDate: 'Proposed lease start date',
  proposedLeaseCommencementDate: 'Proposed lease commencement date',
  leaseTermRequested: 'Lease term requested',
  leaseTermOther: 'Lease term (other)',
  proposedLeaseTermMonths: 'Proposed lease term (months)',
  // Primary – personal
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  dateOfBirth: 'Date of birth',
  moveInDate: 'Move-in date',
  numberOfOccupants: 'Number of occupants',
  currentAddress: 'Current address',
  timeAtCurrentAddress: 'Time at current address (months)',
  lengthAtCurrentAddress: 'Length at current address (months)',
  previousAddress: 'Previous address',
  previousAddressIfLessThan2Years: 'Previous address (if less than 2 years at current)',
  idType: 'ID type',
  idNumber: 'Licence or passport number',
  idState: 'Licence state',
  idCountry: 'Passport country',
  idStateCountry: 'ID state/country',
  // Employment & income
  employment: 'Employment',
  employmentStatus: 'Employment status',
  employer: 'Employer',
  employerName: 'Employer name',
  position: 'Position',
  lengthOfEmployment: 'Length of employment (months)',
  employerContact: 'Employer contact',
  income: 'Income',
  incomeFrequency: 'Income frequency',
  netWeeklyIncome: 'Net weekly income ($)',
  businessName: 'Business name (self-employed)',
  abn: 'ABN',
  accountantContact: 'Accountant contact',
  notes: 'Notes',
  // Pets
  petsYesNo: 'Any pets?',
  intendToKeepPets: 'Intend to keep pets?',
  petDetails: 'Pet details (type, breed, age)',
  typeOfPet: 'Type of pet',
  breed: 'Breed',
  petAge: 'Pet age',
  numberOfAnimals: 'Number of animals',
  // Rental history
  priorRentalHistory: 'Prior rental history?',
  currentRentalAgency: 'Current agency / landlord',
  currentRentalContact: 'Current rental contact phone',
  currentRentalWeeklyRent: 'Current weekly rent',
  currentRentalLength: 'Length of tenancy',
  reasonForLeaving: 'Reason for leaving',
  previousRentalAgency: 'Previous agency / landlord',
  previousRentalContact: 'Previous rental contact',
  // References
  personalRefName: 'Personal reference – name',
  personalRefRelationship: 'Personal reference – relationship',
  personalRefPhone: 'Personal reference – phone',
  additionalRefName: 'Additional reference – name',
  additionalRefRelationship: 'Additional reference – relationship',
  additionalRefPhone: 'Additional reference – phone',
  // Declarations
  rentalArrears: 'Outstanding rental arrears?',
  tenancyDatabaseListed: 'Listed on tenancy database?',
  tribunalTerminated: 'Tenancy terminated by tribunal?',
  financialDisclosureDetails: 'Financial / disclosure details',
  declarationSignature: 'Signature (full name)',
  declarationDate: 'Declaration date',
  privacyConsent: 'Privacy consent',
};

function formatFieldLabel(key: string): string {
  return APPLICATION_FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

function getFieldEntries(obj: Record<string, unknown> | null | undefined, keysFilter?: string[]): Array<[string, string]> {
  if (!obj || typeof obj !== 'object') return [];
  const entries: Array<[string, string]> = [];
  const keys = keysFilter ?? Object.keys(obj).filter((k) => k !== 'extra' && typeof (obj as any)[k] !== 'object');
  for (const key of keys) {
    if (!(key in obj)) continue;
    const v = (obj as any)[key];
    if (v === undefined || v === null || v === '') continue;
    const label = formatFieldLabel(key);
    entries.push([label, typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)]);
  }
  return entries;
}

const PRIMARY_SECTIONS: Array<{ title: string; keys: string[] }> = [
  { title: 'Property & lease', keys: ['propertyAddress', 'suburb', 'postcode', 'weeklyRent', 'advertisedWeeklyRent', 'bondAmount', 'proposedLeaseStartDate', 'proposedLeaseCommencementDate', 'leaseTermRequested', 'leaseTermOther', 'proposedLeaseTermMonths'] },
  { title: 'Primary applicant – personal', keys: ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'moveInDate', 'numberOfOccupants', 'currentAddress', 'timeAtCurrentAddress', 'lengthAtCurrentAddress', 'previousAddress', 'previousAddressIfLessThan2Years', 'idType', 'idNumber', 'idState', 'idCountry', 'idStateCountry'] },
  { title: 'Primary applicant – employment & income', keys: ['employment', 'employmentStatus', 'employerName', 'employer', 'position', 'lengthOfEmployment', 'employerContact', 'income', 'incomeFrequency', 'netWeeklyIncome', 'businessName', 'abn', 'accountantContact', 'notes'] },
  { title: 'Pets', keys: ['petsYesNo', 'intendToKeepPets', 'petDetails', 'typeOfPet', 'breed', 'petAge', 'numberOfAnimals'] },
  { title: 'Rental history', keys: ['priorRentalHistory', 'currentRentalAgency', 'currentRentalContact', 'currentRentalWeeklyRent', 'currentRentalLength', 'reasonForLeaving', 'previousRentalAgency', 'previousRentalContact'] },
  { title: 'References', keys: ['personalRefName', 'personalRefRelationship', 'personalRefPhone', 'additionalRefName', 'additionalRefRelationship', 'additionalRefPhone'] },
  { title: 'Declarations', keys: ['rentalArrears', 'tenancyDatabaseListed', 'tribunalTerminated', 'financialDisclosureDetails'] },
  { title: 'Declaration', keys: ['declarationSignature', 'declarationDate', 'privacyConsent'] },
];
const ALL_PRIMARY_KEYS = PRIMARY_SECTIONS.flatMap((s) => s.keys);

function renderDetailSection(title: string, entries: Array<[string, string]>) {
  if (entries.length === 0) return null;
  return (
    <Box key={title}>
      <Typography variant="subtitle2" color="primary" gutterBottom>{title}</Typography>
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1.5rem' }}>
        {entries.map(([label, value]) => (
          <React.Fragment key={label}>
            <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
            <Typography component="dd" variant="body2" sx={{ m: 0 }}>{value}</Typography>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

const PropertyRentalApplications: React.FC = () => {
  const { agencySlug, propertyId } = useParams<{ agencySlug: string; propertyId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  const [applications, setApplications] = useState<RentalApplicationSummary[]>([]);
  const [applicationUrl, setApplicationUrl] = useState<string | null>(null);
  const [formSupported, setFormSupported] = useState<boolean | null>(null);
  const [unsupportedMessage, setUnsupportedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedApp, setSelectedApp] = useState<RentalApplicationSummary | null>(null);
  const [outcomeDialog, setOutcomeDialog] = useState<'approve' | 'reject' | 'owner_approve' | 'owner_reject' | null>(null);
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailApp, setDetailApp] = useState<RentalApplicationSummary | null>(null);
  const [detailIndex, setDetailIndex] = useState<number>(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [tenancyDialogApp, setTenancyDialogApp] = useState<RentalApplicationSummary | null>(null);
  const [tenancyGenerating, setTenancyGenerating] = useState(false);
  const [tenancyForm, setTenancyForm] = useState<{ startDate: string; endDate: string; weeklyRent: string; bondAmount: string; paymentMethod: string; includedItems: string }>({ startDate: '', endDate: '', weeklyRent: '', bondAmount: '', paymentMethod: 'Bank transfer', includedItems: '' });
  /** When set, backend returned VIC prescribed form required – show CAV links and upload. */
  const [vicPrescribedForm, setVicPrescribedForm] = useState<{ form1Url: string; form2Url: string; message?: string } | null>(null);
  const [tenancyUploadFile, setTenancyUploadFile] = useState<File | null>(null);
  const [tenancyUploading, setTenancyUploading] = useState(false);

  // Derive whether landlord approval exists on any loaded application (for simple UI hinting).
  const hasOwnerDecision = useMemo(
    () => applications.some((a) => (a as any).ownerOutcome),
    [applications]
  );

  const load = async () => {
    if (!agencySlug || !propertyId) return;
    setError(null);
    setLoading(true);
    try {
      const list = await listApplications(agencySlug, propertyId);
      setApplications(list);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [agencySlug, propertyId]);

  const fetchLinkAndSupport = useCallback(async (copyToClipboard = false) => {
    if (!agencySlug || !propertyId) return;
    setLinkLoading(true);
    try {
      const res = await getApplicationLink(agencySlug, propertyId);
      setApplicationUrl(res.applicationUrl);
      setFormSupported(res.formSupported);
      setUnsupportedMessage(res.unsupportedMessage ?? null);
      if (copyToClipboard) {
        await navigator.clipboard.writeText(res.applicationUrl);
        setMessage({ type: 'success', text: 'Form link copied to clipboard' });
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to get link' });
    } finally {
      setLinkLoading(false);
    }
  }, [agencySlug, propertyId]);

  useEffect(() => {
    fetchLinkAndSupport(false);
  }, [fetchLinkAndSupport]);

  const handleGetLink = () => fetchLinkAndSupport(true);

  const copyUrl = () => {
    if (applicationUrl) {
      navigator.clipboard.writeText(applicationUrl);
      setMessage({ type: 'success', text: 'Form link copied' });
    }
  };

  const openOutcome = (app: RentalApplicationSummary, outcome: 'approve' | 'reject' | 'owner_approve' | 'owner_reject') => {
    setSelectedApp(app);
    setOutcomeDialog(outcome);
    setOutcomeNotes(app.outcomeNotes || '');
    setInternalNotes(app.internalNotes || '');
  };

  const handleSaveOutcome = async () => {
    if (!agencySlug || !propertyId || !selectedApp) return;
    setSaving(true);
    try {
      const isAgentDecision = outcomeDialog === 'approve' || outcomeDialog === 'reject';
      const isOwnerDecision = outcomeDialog === 'owner_approve' || outcomeDialog === 'owner_reject';

      const payload: any = {
        internalNotes: internalNotes || undefined,
      };

      if (isAgentDecision) {
        payload.outcome = outcomeDialog === 'approve' ? 'approved' : 'rejected';
        payload.outcomeNotes = outcomeNotes || undefined;
      }
      if (isOwnerDecision) {
        payload.ownerOutcome = outcomeDialog === 'owner_approve' ? 'approved' : 'rejected';
        payload.ownerOutcomeNotes = outcomeNotes || undefined;
      }

      await updateApplication(agencySlug, propertyId, selectedApp._id, payload);

      let successText = 'Application updated';
      if (outcomeDialog === 'approve') {
        successText = 'Application approved (agent). If landlord approval is not required, the applicant will receive a congratulations email with next steps.';
      } else if (outcomeDialog === 'reject') {
        successText = 'Application rejected (agent).';
      } else if (outcomeDialog === 'owner_approve') {
        successText = 'Application approved by landlord. The applicant will receive a congratulations email with next steps.';
      } else if (outcomeDialog === 'owner_reject') {
        successText = 'Application rejected by landlord.';
      }

      setMessage({ type: 'success', text: successText });
      setOutcomeDialog(null);
      setSelectedApp(null);
      load();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (app: RentalApplicationSummary, status: string) => {
    if (!agencySlug || !propertyId) return;
    try {
      await updateApplication(agencySlug, propertyId, app._id, { status });
      setMessage({ type: 'success', text: 'Status updated' });
      load();
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to update' });
    }
  };

  const [bondLodgedSaving, setBondLodgedSaving] = useState(false);
  const handleMarkBondLodged = async (app: RentalApplicationSummary) => {
    if (!agencySlug || !propertyId) return;
    setBondLodgedSaving(true);
    try {
      await updateApplication(agencySlug, propertyId, app._id, { bondLodged: true });
      setMessage({ type: 'success', text: 'Bond marked as lodged. Applicant(s) have been emailed and can sign the tenancy agreement.' });
      load();
      if (detailApp?._id === app._id) {
        const full = await getApplication(agencySlug, propertyId, app._id);
        setDetailApp(full);
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to mark bond lodged' });
    } finally {
      setBondLodgedSaving(false);
    }
  };

  const openDetails = async (app: RentalApplicationSummary) => {
    if (!agencySlug || !propertyId) return;
    const idx = applications.findIndex((a) => a._id === app._id);
    setDetailIndex(idx >= 0 ? idx : 0);
    setDetailDialogOpen(true);
    setDetailApp(null);
    setDetailLoading(true);
    try {
      const full = await getApplication(agencySlug, propertyId, app._id);
      setDetailApp(full);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to load application details' });
      setDetailDialogOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailDialogOpen(false);
    setDetailApp(null);
  };

  const navigateDetail = async (direction: 'prev' | 'next') => {
    if (!agencySlug || !propertyId || applications.length === 0) return;
    const nextIndex = direction === 'prev' ? detailIndex - 1 : detailIndex + 1;
    if (nextIndex < 0 || nextIndex >= applications.length) return;
    setDetailIndex(nextIndex);
    setDetailApp(null);
    setDetailLoading(true);
    try {
      const full = await getApplication(agencySlug, propertyId, applications[nextIndex]._id);
      setDetailApp(full);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to load application' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleShortlistFromDetail = async () => {
    if (!detailApp) return;
    try {
      await handleUpdateStatus(detailApp, 'shortlisted');
      setDetailApp((prev) => (prev ? { ...prev, status: 'shortlisted' } : null));
      load();
    } catch {
      // message set by handleUpdateStatus
    }
  };

  const openOutcomeFromDetail = (outcome: 'approve' | 'reject') => {
    if (detailApp) {
      setDetailDialogOpen(false);
      setDetailApp(null);
      openOutcome(detailApp, outcome);
    }
  };

  const openTenancyDialog = (app: RentalApplicationSummary) => {
    setTenancyDialogApp(app);
    setTenancyForm({ startDate: '', endDate: '', weeklyRent: '', bondAmount: '', paymentMethod: 'Bank transfer', includedItems: '' });
  };

  const handleGenerateTenancyAgreement = async () => {
    if (!agencySlug || !propertyId || !tenancyDialogApp) return;
    const appId = tenancyDialogApp._id;
    setTenancyGenerating(true);
    try {
      const res = await generateTenancyAgreement(agencySlug, propertyId, appId, {
        termType: 'fixed',
        startDate: tenancyForm.startDate || undefined,
        endDate: tenancyForm.endDate || undefined,
        weeklyRent: tenancyForm.weeklyRent ? Number(tenancyForm.weeklyRent) : undefined,
        bondAmount: tenancyForm.bondAmount ? Number(tenancyForm.bondAmount) : undefined,
        paymentMethod: tenancyForm.paymentMethod || undefined,
        includedItems: tenancyForm.includedItems || undefined,
      });
      setMessage({ type: 'success', text: 'Tenancy agreement generated. You can download it below.' });
      setTenancyDialogApp(null);
      load();
      if (detailApp?._id === appId) {
        const full = await getApplication(agencySlug, propertyId, appId);
        setDetailApp(full);
      }
      if (res.tenancyAgreement?.url) window.open(res.tenancyAgreement.url, '_blank');
    } catch (e: any) {
      const data = e?.response?.data;
      if (e?.response?.status === 400 && data?.code === 'VIC_PRESCRIBED_FORM_REQUIRED') {
        setVicPrescribedForm({
          form1Url: data.form1Url || '',
          form2Url: data.form2Url || '',
          message: data.message,
        });
      } else {
        setMessage({ type: 'error', text: data?.error || e?.message || 'Failed to generate tenancy agreement' });
      }
    } finally {
      setTenancyGenerating(false);
    }
  };

  const handleUploadTenancyAgreement = async () => {
    if (!agencySlug || !propertyId || !tenancyDialogApp || !tenancyUploadFile) return;
    setTenancyUploading(true);
    try {
      const res = await uploadTenancyAgreement(agencySlug, propertyId, tenancyDialogApp._id, tenancyUploadFile);
      setMessage({ type: 'success', text: res.message || 'Tenancy agreement uploaded.' });
      setVicPrescribedForm(null);
      setTenancyUploadFile(null);
      setTenancyDialogApp(null);
      load();
      if (detailApp?._id === tenancyDialogApp._id) {
        const full = await getApplication(agencySlug, propertyId, tenancyDialogApp._id);
        setDetailApp(full);
      }
      if (res.tenancyAgreement?.url) window.open(res.tenancyAgreement.url as string, '_blank');
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to upload tenancy agreement' });
    } finally {
      setTenancyUploading(false);
    }
  };

  const closeTenancyDialog = () => {
    setTenancyDialogApp(null);
    setVicPrescribedForm(null);
    setTenancyUploadFile(null);
  };

  const handleViewTenancyAgreement = async (app: RentalApplicationSummary) => {
    if (!agencySlug || !propertyId) return;
    try {
      const full = await getApplication(agencySlug, propertyId, app._id);
      if (full.tenancyAgreement?.url) {
        window.open(full.tenancyAgreement.url, '_blank');
      } else {
        setMessage({ type: 'error', text: 'Tenancy agreement not found. Prepare it first.' });
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.error || e?.message || 'Failed to open tenancy agreement' });
    }
  };

  if (!agencySlug || !propertyId) return null;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small">
          Back
        </Button>
      </Box>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Rental applications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Share the form link with interested tenants so they can apply to this property. After they submit, they receive their own link and access code to view their application status. Review applications below and record agent and (optionally) landlord approval.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      {formSupported === false && unsupportedMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {unsupportedMessage}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Form link for this property</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Share this link so applicants can start an application for this property. They will receive a separate link and access code after submitting to view their application status.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleGetLink}
            disabled={linkLoading}
          >
            {linkLoading ? 'Generating…' : applicationUrl ? 'Copy link again' : 'Get form link'}
          </Button>
          {applicationUrl && (
            <TextField
              size="small"
              value={applicationUrl}
              readOnly
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={copyUrl} size="small" aria-label="Copy">
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Applications ({applications.length})
          </Typography>
          {applications.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No applications yet. Share the form link above so applicants can apply to this property.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Applicant</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Move-in</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app._id}>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {app.applicant.firstName} {app.applicant.lastName}
                        </Typography>
                        {app.internalNotes && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {app.internalNotes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{app.applicant.email}</Typography>
                        <Typography variant="body2">{app.applicant.phone}</Typography>
                      </TableCell>
                      <TableCell>{app.applicant.moveInDate || '—'}</TableCell>
                      <TableCell>
                        {new Date(app.submittedAt).toLocaleDateString(undefined, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={app.status.replace('_', ' ')}
                            color={statusColor[app.status] || 'default'}
                            size="small"
                          />
                          {(app as any).ownerOutcome === 'approved' && (
                            <Chip label="Landlord approved" size="small" variant="outlined" color="success" />
                          )}
                          {(app as any).ownerOutcome === 'rejected' && (
                            <Chip label="Landlord rejected" size="small" variant="outlined" color="error" />
                          )}
                          {(app.status === 'approved' || app.outcome === 'approved') && (app as any).bondLodgedAt && (
                            <Chip label="Bond lodged" size="small" variant="outlined" color="success" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => openDetails(app)} aria-label="View details">
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {(app.status === 'approved' || app.outcome === 'approved') && (
                          (app as any).tenancyAgreement?.key ? (
                            <Tooltip title="View tenancy agreement">
                              <IconButton size="small" onClick={() => handleViewTenancyAgreement(app)} aria-label="View tenancy agreement">
                                <DescriptionIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Prepare tenancy agreement">
                              <IconButton size="small" onClick={() => openTenancyDialog(app)} aria-label="Prepare tenancy agreement">
                                <DescriptionIcon color="action" />
                              </IconButton>
                            </Tooltip>
                          )
                        )}
                        {app.status !== 'approved' && app.status !== 'rejected' && (
                          <>
                            <Tooltip title="Shortlist">
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateStatus(app, 'shortlisted')}
                              >
                                <PlaylistAddCheckIcon color="action" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Approve (agent decision)">
                              <IconButton size="small" color="success" onClick={() => openOutcome(app, 'approve')}>
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject (agent decision)">
                              <IconButton size="small" color="error" onClick={() => openOutcome(app, 'reject')}>
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={outcomeDialog !== null} onClose={() => setOutcomeDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {outcomeDialog === 'approve' && 'Approve application (agent)'}
          {outcomeDialog === 'reject' && 'Reject application (agent)'}
          {outcomeDialog === 'owner_approve' && 'Approve application (landlord)'}
          {outcomeDialog === 'owner_reject' && 'Reject application (landlord)'}
          {selectedApp && (
            <Typography variant="body2" color="text.secondary">
              {selectedApp.applicant.firstName} {selectedApp.applicant.lastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Internal notes (optional)"
            multiline
            rows={2}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label={
              outcomeDialog === 'approve' || outcomeDialog === 'owner_approve'
                ? 'Approval notes (optional)'
                : 'Reason for rejection (optional)'
            }
            multiline
            rows={2}
            value={outcomeNotes}
            onChange={(e) => setOutcomeNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOutcomeDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={
              outcomeDialog === 'approve' || outcomeDialog === 'owner_approve'
                ? 'success'
                : 'error'
            }
            onClick={handleSaveOutcome}
            disabled={saving}
          >
            {saving
              ? 'Saving…'
              : outcomeDialog === 'approve' || outcomeDialog === 'owner_approve'
              ? 'Approve'
              : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={tenancyDialogApp !== null} onClose={closeTenancyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{vicPrescribedForm ? 'Victoria (VIC) – prescribed form required' : 'Prepare tenancy agreement'}</DialogTitle>
        <DialogContent>
          {tenancyDialogApp && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {tenancyDialogApp.applicant?.firstName} {tenancyDialogApp.applicant?.lastName} · Approved application
            </Typography>
          )}
          {vicPrescribedForm ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                In Victoria, written residential tenancy agreements must use the prescribed forms from Consumer Affairs Victoria. You cannot use a custom contract in place of them.
              </Typography>
              {vicPrescribedForm.message && (
                <Typography variant="body2" color="text.secondary">{vicPrescribedForm.message}</Typography>
              )}
              <Typography variant="subtitle2">Download the official form</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Link href={vicPrescribedForm.form1Url} target="_blank" rel="noopener noreferrer">
                  Form 1 – Residential rental agreement (term of 5 years or less)
                </Link>
                <Link href={vicPrescribedForm.form2Url} target="_blank" rel="noopener noreferrer">
                  Form 2 – Residential rental agreement (term of more than 5 years)
                </Link>
              </Box>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Upload completed agreement</Typography>
              <Typography variant="body2" color="text.secondary">After completing and signing the prescribed form, upload the signed PDF or Word document here (max 20MB).</Typography>
              <Button variant="outlined" component="label" size="small" sx={{ alignSelf: 'flex-start' }}>
                {tenancyUploadFile ? tenancyUploadFile.name : 'Choose file (PDF or .docx)'}
                <input
                  type="file"
                  hidden
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => setTenancyUploadFile(e.target.files?.[0] ?? null)}
                />
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>Optional: fill in below or leave blank to use property/defaults. For NSW we generate a standard agreement; for VIC you will be guided to the prescribed form.</Typography>
              <TextField fullWidth size="small" label="Lease start date" type="date" value={tenancyForm.startDate} onChange={(e) => setTenancyForm((f) => ({ ...f, startDate: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Lease end date" type="date" value={tenancyForm.endDate} onChange={(e) => setTenancyForm((f) => ({ ...f, endDate: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Weekly rent ($)" type="number" value={tenancyForm.weeklyRent} onChange={(e) => setTenancyForm((f) => ({ ...f, weeklyRent: e.target.value }))} inputProps={{ min: 0 }} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Bond amount ($)" type="number" value={tenancyForm.bondAmount} onChange={(e) => setTenancyForm((f) => ({ ...f, bondAmount: e.target.value }))} inputProps={{ min: 0 }} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Payment method" value={tenancyForm.paymentMethod} onChange={(e) => setTenancyForm((f) => ({ ...f, paymentMethod: e.target.value }))} placeholder="e.g. Bank transfer, BPAY" sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="Included items (e.g. parking, furniture)" value={tenancyForm.includedItems} onChange={(e) => setTenancyForm((f) => ({ ...f, includedItems: e.target.value }))} multiline rows={2} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTenancyDialog}>Cancel</Button>
          {vicPrescribedForm ? (
            <Button variant="contained" onClick={handleUploadTenancyAgreement} disabled={!tenancyUploadFile || tenancyUploading}>
              {tenancyUploading ? 'Uploading…' : 'Upload completed agreement'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleGenerateTenancyAgreement} disabled={tenancyGenerating}>
              {tenancyGenerating ? 'Generating…' : 'Generate tenancy agreement'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={detailDialogOpen} onClose={closeDetails} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">Application details</Typography>
            {detailApp && !detailLoading && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {detailApp.applicant?.firstName} {detailApp.applicant?.lastName} · Submitted {new Date(detailApp.submittedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Application ID: {detailApp._id}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(detailApp._id);
                      setMessage({ type: 'success', text: 'Application ID copied' });
                    }}
                    aria-label="Copy application ID"
                  >
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </>
            )}
          </Box>
          {detailApp && !detailLoading && applications.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => navigateDetail('prev')}
                disabled={detailIndex <= 0}
                aria-label="Previous application"
              >
                <NavigateBeforeIcon />
              </IconButton>
              <Typography variant="body2" color="text.secondary">
                {detailIndex + 1} / {applications.length}
              </Typography>
              <IconButton
                size="small"
                onClick={() => navigateDetail('next')}
                disabled={detailIndex >= applications.length - 1}
                aria-label="Next application"
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {detailApp && !detailLoading && (() => {
            const primary = detailApp.primaryApplicant || detailApp.applicant || {};
            const extra = (primary as any).extra || (detailApp.applicant as any)?.extra || {};
            const fullPrimary = { ...primary, ...(typeof extra === 'object' ? extra : {}) } as Record<string, unknown>;
            const otherKeys = Object.keys(fullPrimary).filter((k) => k !== 'extra' && !ALL_PRIMARY_KEYS.includes(k));
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {PRIMARY_SECTIONS.map((section) => {
                  const entries = getFieldEntries(fullPrimary, section.keys);
                  return entries.length > 0 ? renderDetailSection(section.title, entries) : null;
                })}
                {otherKeys.length > 0 && renderDetailSection('Other details', getFieldEntries(fullPrimary, otherKeys))}
                {detailApp.coApplicants && detailApp.coApplicants.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Co-applicants</Typography>
                    {detailApp.coApplicants.map((co, i) => {
                      const coObj = co as Record<string, unknown>;
                      const entries = getFieldEntries(coObj);
                      return (
                        <Box key={i} sx={{ mb: 2, pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
                          <Typography variant="body2" fontWeight="600" gutterBottom>Co-applicant {i + 1}</Typography>
                          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem' }}>
                            {entries.map(([label, value]) => (
                              <React.Fragment key={label}>
                                <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
                                <Typography component="dd" variant="body2" sx={{ m: 0 }}>{value}</Typography>
                              </React.Fragment>
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {detailApp.childOccupants && detailApp.childOccupants.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Child occupants</Typography>
                    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem' }}>
                      {detailApp.childOccupants.map((c, i) => (
                        <React.Fragment key={i}>
                          <Typography component="dt" variant="body2" color="text.secondary">Name</Typography>
                          <Typography component="dd" variant="body2" sx={{ m: 0 }}>{c.fullName}</Typography>
                          {c.dateOfBirth && (
                            <>
                              <Typography component="dt" variant="body2" color="text.secondary">Date of birth</Typography>
                              <Typography component="dd" variant="body2" sx={{ m: 0 }}>{c.dateOfBirth}</Typography>
                            </>
                          )}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                )}
                {detailApp.documents && detailApp.documents.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Supporting documents</Typography>
                    <List dense disablePadding>
                      {detailApp.documents.map((doc, i) => (
                        <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                          <ListItemText
                            primary={doc.name}
                            secondary={doc.documentType ? `${doc.documentType}${doc.applicantRef ? ` · ${doc.applicantRef}` : ''}` : undefined}
                          />
                          {doc.url && (
                            <Link href={doc.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <DownloadIcon fontSize="small" /> Download
                            </Link>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                {(detailApp.internalNotes || detailApp.outcome || detailApp.outcomeNotes || (detailApp as any).ownerOutcome || (detailApp as any).ownerOutcomeNotes) && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Review</Typography>
                    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1.5rem' }}>
                      {detailApp.internalNotes && (
                        <>
                          <Typography component="dt" variant="body2" color="text.secondary">Internal notes</Typography>
                          <Typography component="dd" variant="body2" sx={{ m: 0 }}>{detailApp.internalNotes}</Typography>
                        </>
                      )}
                      {detailApp.outcome && (
                        <>
                          <Typography component="dt" variant="body2" color="text.secondary">Outcome</Typography>
                          <Typography component="dd" variant="body2" sx={{ m: 0 }}>{detailApp.outcome}</Typography>
                        </>
                      )}
                      {detailApp.outcomeNotes && (
                        <>
                          <Typography component="dt" variant="body2" color="text.secondary">Outcome notes</Typography>
                          <Typography component="dd" variant="body2" sx={{ m: 0 }}>{detailApp.outcomeNotes}</Typography>
                        </>
                      )}
                      {(detailApp as any).ownerOutcome && (
                        <>
                          <Typography component="dt" variant="body2" color="text.secondary">Landlord outcome</Typography>
                          <Typography component="dd" variant="body2" sx={{ m: 0 }}>{(detailApp as any).ownerOutcome}</Typography>
                        </>
                      )}
                      {(detailApp as any).ownerOutcomeNotes && (
                        <>
                          <Typography component="dt" variant="body2" color="text.secondary">Landlord notes</Typography>
                          <Typography component="dd" variant="body2" sx={{ m: 0 }}>{(detailApp as any).ownerOutcomeNotes}</Typography>
                        </>
                      )}
                      {detailApp.bondLodgedAt && (
                        <>
                          <Typography component="dt" variant="body2" color="text.secondary">Bond lodged</Typography>
                          <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                            {new Date(detailApp.bondLodgedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {detailApp && (detailApp.status === 'approved' || detailApp.outcome === 'approved') && (
            <>
              {detailApp.tenancyAgreement?.key ? (
                <Button startIcon={<DescriptionIcon />} onClick={() => handleViewTenancyAgreement(detailApp)}>
                  View tenancy agreement
                </Button>
              ) : (
                <Button startIcon={<DescriptionIcon />} onClick={() => { setDetailDialogOpen(false); openTenancyDialog(detailApp); }}>
                  Prepare tenancy agreement
                </Button>
              )}
              {detailApp.bondLodgedAt ? (
                <Chip label={`Bond lodged ${new Date(detailApp.bondLodgedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`} size="small" color="success" variant="outlined" />
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleMarkBondLodged(detailApp)}
                  disabled={bondLodgedSaving}
                >
                  {bondLodgedSaving ? 'Saving…' : 'Mark bond lodged'}
                </Button>
              )}
            </>
          )}
          {detailApp && detailApp.status !== 'approved' && detailApp.status !== 'rejected' && (
            <>
              <Button
                startIcon={<PlaylistAddCheckIcon />}
                onClick={handleShortlistFromDetail}
                disabled={detailApp.status === 'shortlisted'}
              >
                {detailApp.status === 'shortlisted' ? 'Shortlisted' : 'Shortlist'}
              </Button>
              <Button startIcon={<CheckCircleIcon />} color="success" onClick={() => openOutcomeFromDetail('approve')}>
                Approve
              </Button>
              <Button startIcon={<CancelIcon />} color="error" onClick={() => openOutcomeFromDetail('reject')}>
                Reject
              </Button>
              <Button startIcon={<CheckCircleIcon />} color="success" onClick={() => openOutcomeFromDetail('owner_approve')}>
                Approve (landlord)
              </Button>
              <Button startIcon={<CancelIcon />} color="error" onClick={() => openOutcomeFromDetail('owner_reject')}>
                Reject (landlord)
              </Button>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PropertyRentalApplications;
